// src/pages/PrizeStructure/AddPrizeTierModal.tsx
// Modal "+ Thêm hạng giải" — thêm 1 hạng giải mới vào cơ cấu của miền đang chọn. DisplayOrder không nhập ở
// đây, server tự gán vào cuối danh sách (xem CreatePrizeTierConfigCommand bên payout-service).
import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import NumberInput from 'Components/Common/NumberInput';
import { MatchingRule, MatchingPosition, NewPrizeTierConfigInput } from './types';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('prize_structure');

interface Props {
  isOpen: boolean;
  submitting: boolean;
  onSubmit: (tier: NewPrizeTierConfigInput) => void;
  onClose: () => void;
}

const MATCHING_RULE_OPTIONS: { value: MatchingRule; label: string }[] = [
  { value: 'exact', label: st('khop_dung_exact_label') },
  { value: 'last_n', label: st('khop_n_so_cuoi_last_n_label') },
  { value: 'first_n_wrong', label: st('sai_so_dau_khop_n_so_label') },
  { value: 'any_n_wrong', label: st('sai_dung_n_so_bat_ky_label') },
];

const MATCHING_POSITION_OPTIONS: { value: MatchingPosition; label: string }[] = [
  { value: 'all', label: 'all' },
  { value: 'first', label: 'first' },
  { value: 'last', label: 'last' },
];

const emptyForm: NewPrizeTierConfigInput = {
  code: '',
  displayName: '',
  colorTheme: 'primary',
  digitCondition: 2,
  quantity: 1,
  valuePerTicket: 0,
  matchingRule: 'exact',
  matchingPosition: 'all',
  matchingDigits: null,
};

const AddPrizeTierModal: React.FC<Props> = ({ isOpen, submitting, onSubmit, onClose }) => {
  const location = useLocation();
  const [form, setForm] = useState<NewPrizeTierConfigInput>(emptyForm);
  const [error, setError] = useState<string | null>(null);

  // Reset form mỗi khi mở modal mới — tránh giữ lại dữ liệu của lần thêm trước
  const handleClosed = () => {
    setForm(emptyForm);
    setError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.code.trim() || !form.displayName.trim()) {
      setError(st('vui_long_nhap_day_du_ma_text'));
      return;
    }
    if (form.matchingRule !== 'exact' && !form.matchingDigits) {
      setError(st('vui_long_nhap_so_chu_so_text'));
      return;
    }

    setError(null);
    onSubmit({
      ...form,
      code: form.code.trim().toUpperCase(),
      displayName: form.displayName.trim(),
      matchingPosition: form.matchingRule === 'exact' ? null : form.matchingPosition,
      matchingDigits: form.matchingRule === 'exact' ? null : form.matchingDigits,
    });
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} onClosed={handleClosed} centered>
      <Form onSubmit={handleSubmit}>
        <ModalHeader toggle={onClose}>{st('them_hang_giai_moi_title')}</ModalHeader>
        <ModalBody>
          {error && <div className="alert alert-danger py-2">{error}</div>}

          <FormGroup>
            <Label>{st('ma_hang_giai_label')} <span className="text-danger">*</span></Label>
            <Input
              type="text"
              placeholder={st('vd_dac_biet_2_placeholder')}
              value={form.code}
              data-action-code="input-code"
              data-action-name="Mã hạng giải"
              disabled={!PageActionPermission.canEnable(location.pathname, 'input-code')}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            />
            <small className="text-muted">{st('chu_hoa_so_va_dau_gach_hint')}</small>
          </FormGroup>

          <FormGroup>
            <Label>{st('ten_hang_giai_label')} <span className="text-danger">*</span></Label>
            <Input
              type="text"
              placeholder={st('vd_giai_dac_biet_2_placeholder')}
              value={form.displayName}
              data-action-code="input-display-name"
              data-action-name="Tên hạng giải"
              disabled={!PageActionPermission.canEnable(location.pathname, 'input-display-name')}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            />
          </FormGroup>

          <div className="row">
            <FormGroup className="col-6">
              <Label>{st('so_luong_label')}</Label>
              <NumberInput
                value={form.quantity}
                data-action-code="input-quantity"
                data-action-name="Số lượng giải"
                disabled={!PageActionPermission.canEnable(location.pathname, 'input-quantity')}
                onChange={(val) => setForm({ ...form, quantity: val })}
              />
            </FormGroup>
            <FormGroup className="col-6">
              <Label>{st('gia_tri_ve_label')}</Label>
              <NumberInput
                value={form.valuePerTicket}
                data-action-code="input-value-per-ticket"
                data-action-name="Giá trị mỗi vé"
                disabled={!PageActionPermission.canEnable(location.pathname, 'input-value-per-ticket')}
                onChange={(val) => setForm({ ...form, valuePerTicket: val })}
              />
            </FormGroup>
          </div>

          <div className="row">
            <FormGroup className="col-6">
              <Label>{st('so_chu_so_trung_label')}</Label>
              <NumberInput
                value={form.digitCondition}
                data-action-code="input-digit-condition"
                data-action-name="Số chữ số trúng"
                disabled={!PageActionPermission.canEnable(location.pathname, 'input-digit-condition')}
                onChange={(val) => setForm({ ...form, digitCondition: val })}
              />
            </FormGroup>
            <FormGroup className="col-6">
              <Label>{st('mau_hien_thi_label')}</Label>
              <Input
                type="select"
                value={form.colorTheme}
                data-action-code="input-color-theme"
                data-action-name="Màu hiển thị"
                disabled={!PageActionPermission.canEnable(location.pathname, 'input-color-theme')}
                onChange={(e) => setForm({ ...form, colorTheme: e.target.value as 'danger' | 'primary' })}
              >
                <option value="primary">{st('primary_option')}</option>
                <option value="danger">{st('danger_option')}</option>
              </Input>
            </FormGroup>
          </div>

          <FormGroup>
            <Label>{st('quy_tac_khop_giai_label')}</Label>
            <Input
              type="select"
              value={form.matchingRule}
              data-action-code="input-matching-rule"
              data-action-name="Quy tắc khớp giải"
              disabled={!PageActionPermission.canEnable(location.pathname, 'input-matching-rule')}
              onChange={(e) => setForm({ ...form, matchingRule: e.target.value as MatchingRule })}
            >
              {MATCHING_RULE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Input>
          </FormGroup>

          {form.matchingRule !== 'exact' && (
            <div className="row">
              <FormGroup className="col-6">
                <Label>{st('vi_tri_khop_label')}</Label>
                <Input
                  type="select"
                  value={form.matchingPosition ?? 'all'}
                  data-action-code="input-matching-position"
                  data-action-name="Vị trí khớp"
                  disabled={!PageActionPermission.canEnable(location.pathname, 'input-matching-position')}
                  onChange={(e) => setForm({ ...form, matchingPosition: e.target.value as MatchingPosition })}
                >
                  {MATCHING_POSITION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Input>
              </FormGroup>
              <FormGroup className="col-6">
                <Label>{st('so_chu_so_khop_n_label')}</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={form.matchingDigits ?? ''}
                  data-action-code="input-matching-digits"
                  data-action-name="Số chữ số khớp (N)"
                  disabled={!PageActionPermission.canEnable(location.pathname, 'input-matching-digits')}
                  onChange={(e) => {
                    const digitsOnly = e.target.value.replace(/\D/g, '');
                    setForm({ ...form, matchingDigits: digitsOnly === '' ? null : Number(digitsOnly) });
                  }}
                />
              </FormGroup>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" type="button" onClick={onClose}>
            {st('dong_button')}
          </Button>
          {PageActionPermission.canDisplay(location.pathname, 'button-submit') && (
          <Button
            color="primary"
            type="submit"
            data-action-code="button-submit"
            data-action-name="Thêm hạng giải"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                {st('dang_them_text')}
              </>
            ) : (
              st('them_hang_giai_text')
            )}
          </Button>
          )}
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddPrizeTierModal;
